// client/src/components/BulkImportDialog.js
// Adds many products at once from a spreadsheet saved as CSV, with their photos.
import React, { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { API_BASE_URL } from '../utils/media';

const TEMPLATE_COLUMNS = [
  'name', 'description', 'price', 'compareAtPrice', 'category', 'subcategory', 'quantity', 'image',
  'build_type', 'power_type', 'scale', 'wingspan_mm', 'battery_cells', 'battery_mah', 'channels',
  'compatible_with', 'also_needs', 'condition_grade', 'usage_notes',
];
const TEMPLATE_EXAMPLE = [
  'Example Trainer Plane', 'Foam trainer with stabilisation', '3499', '', 'RC Planes', 'Trainers', '5', 'trainer.jpg',
  'RTF', 'Electric', '', '1200', '3', '2200', '6', '', 'LiPo charger', '', '',
];
const MAX_PHOTOS = 200;
const MAX_TOTAL_MB = 55; // The web server turns away larger uploads.

const BulkImportDialog = ({ open, onClose, token, onImported }) => {
  const [csv, setCsv] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const totalMb = (photos.reduce((sum, file) => sum + file.size, 0) + (csv?.size || 0)) / (1024 * 1024);

  const close = () => {
    setCsv(null);
    setPhotos([]);
    setError('');
    setResult(null);
    onClose();
  };

  const downloadTemplate = () => {
    const quote = (value) => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
    const text = `${TEMPLATE_COLUMNS.join(',')}\n${TEMPLATE_EXAMPLE.map(quote).join(',')}\n`;
    const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'product-import-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    setError('');
    setBusy(true);
    try {
      const body = new FormData();
      body.append('file', csv);
      photos.forEach((photo) => body.append('images', photo));
      const response = await fetch(`${API_BASE_URL}/api/seller/products/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'The import failed. Check each photo is under 5 MB.');
      setResult(data);
      onImported();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const tooMany = photos.length > MAX_PHOTOS;
  const tooBig = totalMb > MAX_TOTAL_MB;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Import products</DialogTitle>
      <DialogContent>
        {result ? (
          <Stack spacing={1.5}>
            <Alert severity={result.created > 0 ? 'success' : 'warning'}>Added {result.created} product{result.created === 1 ? '' : 's'}.</Alert>
            {result.errors?.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" fontWeight={700} gutterBottom>Please check these:</Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 220, overflowY: 'auto' }}>
                  {result.errors.map((message) => <li key={message}><Typography variant="body2">{message}</Typography></li>)}
                </Box>
              </Alert>
            )}
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            {error && <Alert severity="error">{error}</Alert>}
            <Box>
              <Typography variant="subtitle1">1. Fill in the template</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Edit it in Excel and save it as CSV. Only name and price are required. In the image column, put a photo's exact file name.
              </Typography>
              <Button size="small" variant="outlined" onClick={downloadTemplate}>Download template</Button>
            </Box>
            <Box>
              <Typography variant="subtitle1">2. Choose your CSV file</Typography>
              <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />} sx={{ mt: 1 }}>
                {csv ? csv.name : 'Choose CSV'}
                <input hidden type="file" accept=".csv" onChange={(e) => { setCsv(e.target.files[0] || null); e.target.value = ''; }} />
              </Button>
            </Box>
            <Box>
              <Typography variant="subtitle1">3. Add the photos (optional)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                JPG, PNG or WebP, up to 5 MB each. Large catalogues are best imported in batches.
              </Typography>
              <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />}>
                {photos.length > 0 ? `${photos.length} photo${photos.length === 1 ? '' : 's'} (${totalMb.toFixed(1)} MB)` : 'Choose photos'}
                <input hidden multiple type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => { setPhotos([...e.target.files]); e.target.value = ''; }} />
              </Button>
              {(tooMany || tooBig) && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  That's more than one import can take (up to {MAX_PHOTOS} photos and {MAX_TOTAL_MB} MB). Please split it into smaller batches.
                </Alert>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{result ? 'Done' : 'Cancel'}</Button>
        {!result && (
          <Button variant="contained" onClick={handleImport} disabled={!csv || busy || tooMany || tooBig}>
            {busy ? 'Importing…' : 'Import'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportDialog;
